import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { CustomButton } from "@/components/custom/custom-button";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { useRoutingConfig } from "@/hooks/use-routing-config";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormCheckList } from "@/components/custom/custom-form-check-list";
import { createRoutingProfile } from "@/services/tech.service";
import { createProfileSchema } from "@/schemas/routing.schema";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";
import { CustomFormSwitch } from "@/components/custom/custom-form-switch";

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    maxWidth: 540,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
  flexShrink: 0,
}));

const CreateRoutingProfileDialog = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data: configData, isLoading: configLoading } = useRoutingConfig(open);

  const config = configData?.data ?? {};

  const formik = useFormik({
    initialValues: {
      precedence: "",
      selectionStrategy: "ALL",
      routingTypes: [],
      cardTypes: [],
      processors: [],
      isActive: true,
      forcePrecedence: false,
    },
    validationSchema: createProfileSchema,
    enableReinitialize: false,
    onSubmit: (values) => {
      mutation.mutate({
        precedence: Number(values.precedence),
        selectionStrategy: values.selectionStrategy,
        routingTypes: values.routingTypes,
        cardTypes: values.cardTypes,
        processors: values.processors,
        isActive: values.isActive,
        forcePrecedence: values.forcePrecedence,
      });
    },
  });

  const mutation = useMutation({
    mutationFn: createRoutingProfile,

    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Profile created successfully", "success"),
      );
      queryClient.invalidateQueries({ queryKey: ["routing-profiles"] });
      onClose();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    mutation.reset();
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2.5, px: 2.5, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper>
            <AddCircleOutlineIcon
              sx={{ color: "primary.main", fontSize: 22 }}
            />
          </IconWrapper>
          <Box>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              New Routing Profile
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              Configure precedence, strategy and routing rules.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormTextInput
                formik={formik}
                name="precedence"
                label="Precedence"
                type="number"
                isDisabled={mutation.isPending}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormSelectInput
                formik={formik}
                name="selectionStrategy"
                label="Selection Strategy"
                isDisabled={mutation.isPending}
                options={[
                  { value: "ALL", label: "ALL" },
                  { value: "ONE", label: "ONE" },
                ]}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <CustomFormSwitch
              formik={formik}
              name="forcePrecedence"
              label="Force Precedence"
              disabled={mutation.isPending}
            />
          </Box>

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Routing Types" />
          ) : (
            <FormCheckList
              formik={formik}
              name="routingTypes"
              label="Routing Types"
              options={config.routingTypes ?? []}
            />
          )}

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Card Types" />
          ) : (
            <FormCheckList
              formik={formik}
              name="cardTypes"
              label="Card Types"
              options={config.cardTypes ?? []}
            />
          )}

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Processors" />
          ) : (
            <FormCheckList
              formik={formik}
              name="processors"
              label="Processors"
              options={config.processors ?? []}
            />
          )}
        </Box>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {mutation.error?.message ?? "Failed to create profile. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          size="small"
          onClick={formik.handleSubmit}
          disabled={mutation.isPending || !formik.dirty}
          sx={{ minWidth: 130 }}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Creating...
            </Box>
          ) : (
            "Create Profile"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

const ChecklistSkeleton = ({ label }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {[1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width={90}
          height={48}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Box>
  </Box>
);

export default CreateRoutingProfileDialog;
